import React from 'react';
import { getAbilityTheme } from '../../utils/abilityTheme';
import communicationIcon from '../../assets/ability/communication-icon.png';
import quantitativeReasoningIcon from '../../assets/ability/quantitative-reasoning-icon.png';

/**
 * Inline SVG infographics for each core ability paper tile (flat pastel styling).
 * @param {{ variant: string, className?: string, theme?: import('../../utils/abilityTheme').AbilityTheme }} props
 */
function AbilityInfographic({ variant, className = '', theme: themeProp, compact = false }) {
  const theme = themeProp || getAbilityTheme(variant);
  const { surface, ink, inkMuted } = theme;
  const sizeClass = compact
    ? 'h-auto w-auto max-h-[52px] max-w-[80px] object-contain object-center'
    : 'w-full h-full max-h-[140px] max-w-[200px]';
  const shared = `${sizeClass} ${className}`.trim();

  switch (variant) {
    case 'quantitative-reasoning':
      return (
        <img
          src={quantitativeReasoningIcon}
          alt=""
          className={`object-contain ${shared}`}
          aria-hidden
        />
      );

    case 'logical-reasoning':
      return (
        <svg
          className={shared}
          viewBox="0 0 200 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <rect x="70" y="20" width="60" height="36" rx="8" fill={surface} />
          <rect x="30" y="88" width="56" height="32" rx="8" fill={surface} />
          <rect x="114" y="88" width="56" height="32" rx="8" fill={surface} />
          <path d="M100 56 L62 88" stroke={inkMuted} strokeWidth="2.5" />
          <path d="M100 56 L138 88" stroke={inkMuted} strokeWidth="2.5" />
          <circle cx="100" cy="38" r="6" fill={surface} stroke={inkMuted} strokeWidth="2" />
          <path d="M88 34 h24 M100 34 v8" stroke={ink} strokeWidth="2" strokeLinecap="round" />
          <text x="58" y="108" textAnchor="middle" fill={ink} fontSize="9" fontWeight="700">
            A
          </text>
          <text x="142" y="108" textAnchor="middle" fill={ink} fontSize="9" fontWeight="700">
            B
          </text>
        </svg>
      );

    case 'critical-thinking':
      return (
        <svg
          className={shared}
          viewBox="0 0 200 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <path d="M100 24 L118 76 H82 Z" fill={surface} stroke={inkMuted} strokeWidth="2" />
          <rect x="52" y="76" width="96" height="12" rx="6" fill={surface} />
          <rect x="72" y="96" width="56" height="28" rx="6" fill={surface} />
          <circle cx="76" cy="110" r="10" fill={surface} stroke={inkMuted} strokeWidth="2" />
          <circle cx="124" cy="110" r="10" fill={surface} stroke={ink} strokeWidth="2" />
          <path d="M72 88 L128 88" stroke={inkMuted} strokeWidth="2" />
          <path d="M100 40 L108 58 H92 Z" fill={inkMuted} />
        </svg>
      );

    case 'verbal-reasoning':
      return (
        <svg
          className={shared}
          viewBox="0 0 200 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden
        >
          <rect x="28" y="36" width="88" height="48" rx="12" fill={surface} />
          <path d="M116 60 L132 48 L132 72 Z" fill={surface} />
          <rect x="108" y="52" width="64" height="40" rx="12" fill={surface} />
          <rect x="40" y="50" width="48" height="4" rx="2" fill={inkMuted} />
          <rect x="40" y="60" width="64" height="4" rx="2" fill={inkMuted} />
          <rect x="40" y="70" width="56" height="4" rx="2" fill={inkMuted} />
          <rect x="120" y="60" width="40" height="4" rx="2" fill={inkMuted} />
          <rect x="120" y="70" width="32" height="4" rx="2" fill={inkMuted} />
          <text x="52" y="100" fontSize="22" fontWeight="700" fill={ink}>
            Aa
          </text>
        </svg>
      );

    case 'communication':
      return (
        <img
          src={communicationIcon}
          alt=""
          className={`object-contain ${shared}`}
          aria-hidden
        />
      );

    default:
      return (
        <svg className={shared} viewBox="0 0 200 140" aria-hidden>
          <circle cx="100" cy="70" r="40" fill={surface} stroke={inkMuted} strokeWidth="2" />
        </svg>
      );
  }
}

export default AbilityInfographic;
